import { Box, Divider, Stack, Theme, lighten, useTheme } from "@mui/material";
import React, { CSSProperties } from "react";
import { AnimatedCardProps } from "./AnimatedCard";

export function DefaultAnimatedCard(props: AnimatedCardProps) {
  const cardWrapperRef = React.useRef<HTMLElement>(null);
  const footerRef = React.useRef<HTMLElement>(null);
  const [isHovered, setIsHovered] = React.useState(false);
  const [backFaceHeight, setBackFaceHeight] = React.useState("100%");
  const resizeObserverRef = React.useRef<ResizeObserver | null>(null);

  const onMouseEnter = (e: any) => {
    setIsHovered(true);
    props?.onMouseEnter?.(e);
  };

  const onMouseLeave = (e: any) => {
    setIsHovered(false);
    props?.onMouseLeave?.(e);
  };

  const onClick = (e: React.MouseEvent) => {
    e?.stopPropagation?.();
    props?.onClick?.(e); // Trigger wrapper's onClick
  };

  const handleUpdateBackFaceHeight = React.useCallback(() => {
    if (props?.isDisabledHoverAnim) return;
    if (!isHovered) return setBackFaceHeight("100%");
    const cardHeight = cardWrapperRef?.current?.clientHeight || 0;
    const footerHeight = footerRef?.current?.scrollHeight || 0; // Use scrollHeight instead of clientHeight
    setBackFaceHeight(`${cardHeight + footerHeight}px`);
  }, [isHovered, props?.isDisabledHoverAnim]);

  // Use ResizeObserver to track size changes
  React.useEffect(() => {
    if (!cardWrapperRef.current || !footerRef.current) return;
    resizeObserverRef.current = new ResizeObserver(() => {
      handleUpdateBackFaceHeight();
    });
    resizeObserverRef.current.observe(cardWrapperRef.current);
    resizeObserverRef.current.observe(footerRef.current);
    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [handleUpdateBackFaceHeight]);

  // Also update on hover state changes
  React.useLayoutEffect(() => {
    handleUpdateBackFaceHeight();
  }, [isHovered, handleUpdateBackFaceHeight]);

  return (
    <AnimatedCardContainer
      ref={cardWrapperRef}
      cardProps={props}
      isHovered={isHovered}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {props.renderBody?.()}

      <Box flexGrow={1} />

      {!props?.isFooterHidden && props?.renderFooter ? (
        <AnimatedCardFooter
          ref={footerRef}
          isHovered={isHovered}
          cardProps={props}
        >
          {props?.renderFooter?.()}
        </AnimatedCardFooter>
      ) : null}

      <AnimatedCardBackface
        height={backFaceHeight}
        isSelected={props?.isSelected}
        isHovered={isHovered}
      />
    </AnimatedCardContainer>
  );
}

const AnimatedCardContainer = React.forwardRef(function (
  props: {
    cardProps: AnimatedCardProps;
    isHovered?: boolean;
    onClick?: (e: any) => void;
    onMouseEnter?: (e: any) => void;
    onMouseLeave?: (e: any) => void;
    children?: React.ReactNode;
  },
  ref
) {
  const theme = useTheme();

  const calcContainerStyles = () => {
    const baseStyles = {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      userSelect: "none",
      opacity: props?.cardProps?.isDisabled ? 0.5 : 1,
      pointerEvents: props?.cardProps?.isDisabled ? "none" : "all",
      borderRadius: `10px 10px ${
        props?.isHovered && !props?.cardProps?.isDisabledHoverAnim
          ? "0px  0px"
          : "10px 10px"
      }`,
      backgroundColor: theme.palette.background.paper,
      cursor: "pointer",
      width: "100%",
      height: "max-content",
    };

    if (props?.cardProps?.isDisabledHoverAnim) {
      return {
        ...baseStyles,
        transform: "none",
        transition: "none",
        transformOrigin: "none",
      };
    } else {
      return {
        ...baseStyles,
        zIndex: props?.isHovered ? 10 : 1,
        transform: props?.isHovered ? "scale(1.05)" : "scale(1)",
        transformOrigin: "center center",
        transition: "transform 0.3s cubic-bezier(.27,.29,0,1)",
      };
    }
  };

  return (
    <Box
      className={"animated-card-container"}
      id={props?.cardProps?.domId}
      ref={ref}
      onClick={props?.onClick}
      onMouseEnter={props?.onMouseEnter}
      onMouseLeave={props?.onMouseLeave}
      sx={{ ...calcContainerStyles(), ...props?.cardProps?.style?.wrapper }}
    >
      {props?.children}
    </Box>
  );
});
AnimatedCardContainer.displayName = "AnimatedCardContainer";

const AnimatedCardFooter = React.forwardRef(function (
  props: {
    cardProps: AnimatedCardProps;
    isHovered?: boolean;
    children?: React.ReactNode;
  },
  ref
) {
  const theme = useTheme();

  const calcFooterStyles = () => {
    const baseStyles = {
      alignSelf: "flex-end",
      width: "100%",
      borderRadius: "0 0 10px 10px",
      backgroundColor: theme.palette.background.paper,
      boxSizing: "border-box",
    };

    if (props?.cardProps?.isDisabledHoverAnim) {
      return {
        ...baseStyles,
        position: "relative",
        top: "unset",
        height: "max-content",
        backgroundColor: theme.palette.background.paper,
        transform: "none",
        transition: "none",
        transformOrigin: "none",
      };
    } else {
      return {
        ...baseStyles,
        position: "absolute",
        top: "100%",
        height: props?.isHovered ? "max-content" : 0,
        transform: props?.isHovered ? "scaleY(1)" : "scaleY(0)",
        transition: "transform 0.2s ease, height 0.2s ease",
        transformOrigin: "top",
      };
    }
  };

  const calcDividerStyles = () => {
    return {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      transition: "all 0.2s ease",
    };
  };

  return (
    <Box ref={ref} className="animated-card-footer" sx={calcFooterStyles()}>
      {props?.cardProps?.isDisabledHoverAnim || props?.isHovered ? (
        <Divider sx={calcDividerStyles()} />
      ) : null}
      {props?.children}
    </Box>
  );
});
AnimatedCardFooter.displayName = "AnimatedCardFooter";

const AnimatedCardBackface = (props: {
  height: CSSProperties["height"];
  isSelected?: boolean;
  isHovered?: boolean;
}) => {
  const theme = useTheme();

  const calcBackfaceStyles = () => {
    const baseShadow = `none`;
    const hoverShadow = `0 0 0 6px ${lighten(theme.palette.primary.main, 0.8)}`;
    const baseOutline = `1px solid ${theme.palette.divider}`;
    const selectedOutline = `2px solid ${theme.palette.primary.main}`;

    return {
      position: "absolute",
      zIndex: -1,
      top: 0,
      left: 0,
      width: "100%",
      height: props?.height || "100%",
      pointerEvents: "none",
      borderRadius: "10px",
      outline: props?.isSelected ? selectedOutline : baseOutline,
      boxShadow: props?.isHovered ? hoverShadow : baseShadow,
      transition: `box-shadow 0.2s ease, height 0.2s ease, outline 0.2s ease`,
      backgroundColor: theme.palette.background.paper,
    };
  };

  return <Box className="animated-card-backface" sx={calcBackfaceStyles()} />;
};
